/**
 * Error Boundary Components
 * 
 * Provides graceful error handling for React components.
 * Catches JavaScript errors anywhere in the component tree and displays fallback UI.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Logger } from '@shared/lib/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console and external service
    Logger.error('ErrorBoundary caught an error', error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: 'ErrorBoundary'
    });

    this.setState({ error, errorInfo });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    if (hasError && resetOnPropsChange) {
      if (resetKeys && prevProps.resetKeys) {
        const hasResetKeyChanged = resetKeys.some((key, index) => 
          key !== prevProps.resetKeys?.[index]
        );
        
        if (hasResetKeyChanged) {
          this.resetErrorBoundary();
        }
      }
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  resetErrorBoundary = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }

    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Card className="m-4 border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Something went wrong
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            
            {this.props.showDetails && this.state.error && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer font-medium">Error Details</summary>
                <pre className="mt-2 whitespace-pre-wrap bg-muted p-2 rounded">
                  {this.state.error.stack}
                </pre>
              </details>
            )}

            <div className="flex gap-2">
              <Button 
                onClick={this.resetErrorBoundary}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Try again
              </Button>
              
              <Button 
                onClick={() => window.location.href = '/'}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Home className="h-4 w-4" />
                Go home
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}

/**
 * Feature-specific error boundary
 */
export const FeatureErrorBoundary = ({ 
  children, 
  feature 
}: { 
  children: ReactNode; 
  feature: string;
}) => (
  <ErrorBoundary
    fallback={
      <div className="p-4 text-center">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-destructive" />
        <h3 className="font-medium text-destructive mb-1">
          Unable to load {feature}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          There was an error loading this feature. Please try refreshing the page.
        </p>
        <Button 
          onClick={() => window.location.reload()}
          variant="outline"
          size="sm"
        >
          Refresh page
        </Button>
      </div>
    }
    onError={(error, errorInfo) => {
      Logger.error(`Feature error in ${feature}`, error, {
        feature,
        componentStack: errorInfo.componentStack
      });
    }}
  >
    {children}
  </ErrorBoundary>
);

/**
 * Route-specific error boundary
 */
export const RouteErrorBoundary = ({ 
  children 
}: { 
  children: ReactNode;
}) => (
  <ErrorBoundary
    fallback={
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <CardTitle className="text-xl">Page Error</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Something went wrong while loading this page.
            </p>
            <div className="flex gap-2 justify-center">
              <Button 
                onClick={() => window.history.back()}
                variant="outline"
              >
                Go back
              </Button>
              <Button 
                onClick={() => window.location.href = '/'}
              >
                Go home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    }
    onError={(error, errorInfo) => {
      Logger.error('Route error', error, {
        route: window.location.pathname,
        componentStack: errorInfo.componentStack
      });
    }}
  >
    {children}
  </ErrorBoundary>
);

/**
 * Form-specific error boundary
 */
export const FormErrorBoundary = ({ 
  children 
}: { 
  children: ReactNode;
}) => (
  <ErrorBoundary
    fallback={
      <div className="p-4 border border-destructive rounded-md bg-destructive/5">
        <div className="flex items-center gap-2 text-destructive mb-2">
          <AlertCircle className="h-4 w-4" />
          <span className="font-medium">Form Error</span>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          There was an error with this form. Please try again.
        </p>
        <Button 
          onClick={() => window.location.reload()}
          variant="outline"
          size="sm"
        >
          Reload form
        </Button>
      </div>
    }
    onError={(error, errorInfo) => {
      Logger.error('Form error', error, {
        componentStack: errorInfo.componentStack
      });
    }}
  >
    {children}
  </ErrorBoundary>
);

/**
 * Data table error boundary
 */
export const DataTableErrorBoundary = ({ 
  children 
}: { 
  children: ReactNode;
}) => (
  <ErrorBoundary
    fallback={
      <div className="p-8 text-center">
        <AlertCircle className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
        <h3 className="font-medium mb-2">Unable to load data</h3>
        <p className="text-sm text-muted-foreground mb-4">
          There was an error loading the table data.
        </p>
        <Button 
          onClick={() => window.location.reload()}
          variant="outline"
          size="sm"
        >
          Retry
        </Button>
      </div>
    }
    onError={(error, errorInfo) => {
      Logger.error('Data table error', error, {
        componentStack: errorInfo.componentStack
      });
    }}
  >
    {children}
  </ErrorBoundary>
);

/**
 * Hook for error boundary reset
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error: Error) => {
    setError(error);
    Logger.error('Captured error in useErrorHandler', error);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return { captureError, resetError };
};